import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Calendar, User } from 'lucide-react';
import { taskService, Task } from '../services/taskService';
import { LoadingSpinner } from '../components/LoadingSpinner';

type TabFilter = 'all' | 'active' | 'completed';

export const TasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const navigate = useNavigate();

  const fetchTasks = useCallback(async () => {
    try {
      const res = await taskService.getTasks();
      setTasks(res.data || []);
    } catch {
      /* keep empty */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const activeTasks = tasks.filter((t) => t.task_status === 'pending');
  const completedTasks = tasks.filter((t) => t.task_status === 'completed');
  const highPriority = activeTasks.filter((t) => t.task_priority <= 3);

  const filtered =
    activeTab === 'active' ? activeTasks
    : activeTab === 'completed' ? completedTasks
    : tasks;

  const priorityLabel = (priority: number) => {
    if (priority <= 3) return { text: 'high', color: 'text-red-600' };
    if (priority <= 6) return { text: 'medium', color: 'text-amber-600' };
    return { text: 'low', color: 'text-gray-400' };
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
  };

  const tabs: { id: TabFilter; label: string }[] = [
    { id: 'all', label: `All (${tasks.length})` },
    { id: 'active', label: `Active (${activeTasks.length})` },
    { id: 'completed', label: `Completed (${completedTasks.length})` },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
        <p className="text-gray-500 mt-1">Manage your coaching tasks and priorities</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active Tasks</p>
          <p className="text-2xl font-bold text-gray-900">{activeTasks.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-green-600">{completedTasks.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">High Priority</p>
          <p className="text-2xl font-bold text-red-600">{highPriority.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-6" aria-label="Task tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors min-h-[44px] ${
                activeTab === tab.id
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No tasks to show</p>
          </div>
        ) : (
          filtered.map((task) => {
            const isCompleted = task.task_status === 'completed';
            const priority = priorityLabel(task.task_priority);
            const playerId = task.metadata?.player_id;
            const isClickable = !isCompleted && !!playerId;
            return (
              <div
                key={task.task_id}
                onClick={() => isClickable && navigate(`/assess/${playerId}`)}
                role={isClickable ? 'link' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={(e) => { if (isClickable && (e.key === 'Enter' || e.key === ' ')) navigate(`/assess/${playerId}`); }}
                className={`bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-4 ${isCompleted ? 'opacity-60' : ''} ${isClickable ? 'cursor-pointer hover:border-green-300 hover:shadow-sm transition-all' : ''}`}
              >
                {/* Status indicator — not interactive, tasks complete via their action */}
                <div className="mt-0.5 flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {isCompleted
                    ? <CheckCircle2 className="h-6 w-6 text-green-600" />
                    : <Circle className="h-6 w-6 text-gray-300" />
                  }
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${isCompleted ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {task.task_title}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">{task.task_description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(task.due_date || task.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3.5 w-3.5" />
                      Coach
                    </span>
                  </div>
                </div>

                {/* Priority */}
                <span className={`text-xs font-medium flex-shrink-0 ${priority.color}`}>
                  {priority.text}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
